<template>
  <div
    v-if="coachingStore.hasAssignedCoach"
    class="i-coaching-conirmation"
  >
    <iBackground
      :background-image="bgGradient"
      image-position="center center"
    />
    <iReportNavbar
      :core-items="navigationItemsData.coreItems"
      :profile-items="navigationItemsData.profileItems"
      :support-items="navigationItemsData.supportItems"
    />

    <div class="content">
      <section>
        <div>
          <div
            v-if="!isCoachingConfirmedPage"
            class="head"
          >
            🏆 Your Session With {{ assignedCoachFirstName }} is <span class="gradient">Confirmed</span> ...
          </div>
          <div
            v-if="isCoachingConfirmedPage"
            class="head"
          >
            You're all set. Now it's time to dive in.
          </div>
          <iChecklist />
        </div>
        <div class="support">
          If you have any questions or concerns reach out to us at:
          <span
            class="gradient"
            :class="{ mini: isCoachingConfirmedPage }"
            >support@jobtest.org</span
          >
        </div>
        <iConfirmFooter />
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue"
import { useRoute } from "vue-router"
import { useCoachingStore } from "@/stores/coaching"

import navigationItems from "@/composables/navigation"
import { GlobalVariables } from "@/helpers/globalVariables"
import GtmService, { UserFlow } from "@/services/gtmService"

// COMPONENTS
import iConfirmFooter from "@/components/coaching/confirmation/ConfirmFooter.vue"
import iChecklist from "@/components/coaching/confirmation/Checklist.vue"
import iReportNavbar from "@/components/report/navbar/ReportNavbar.vue"
import iBackground from "@/components/global/Background.vue"

// ASSETS
import bgGradient from "@/assets/confirm/bg-gradient-multi.png"
import StripePrice from "@/shared/enums/StripePrice"

// STORES AND ROUTES
const activeRoute = useRoute()
const coachingStore = useCoachingStore()

// COMPUTED
const navigationItemsData = computed(() => navigationItems())
const assignedCoach = computed(() => coachingStore.getAssignedCoach)
const assignedCoachFirstName = computed(() => assignedCoach.value?.fullName?.split(" ")[0])
const isCoachingConfirmedPage = computed(
  () => activeRoute.name === GlobalVariables.urls.coaching.children.confirmed.routeName
)

// FUNCTIONS
async function reportOrderToGoogleTagManager() {
  const urlParams = new URLSearchParams(window.location.search)
  const sessionId = urlParams.get("session_id")
  const lastSessionId = localStorage.getItem("last-checkout-session-id")
  const valueAsString = urlParams.get("val")
  const value = valueAsString ? parseFloat(valueAsString) : 0

  if (!sessionId || sessionId == lastSessionId) return

  localStorage.setItem("last-checkout-session-id", sessionId)
  try {
    await GtmService.purchaseEvent(UserFlow.COACHING, sessionId, value)
  } catch (error) {
    console.error("Error reporting coaching purchase to Google Tag Manager", error)
  }
}

async function reportInvoiceChargeToGoogleTagManager() {
  const urlParams = new URLSearchParams(window.location.search)
  const valueAsString = urlParams.get("val")
  const invoiceId = urlParams.get("invoiceId") || ""
  const value = valueAsString ? parseFloat(valueAsString) : 0
  const item = urlParams.get("item")
  const lastInvoicedItem = localStorage.getItem("last-invoiced-item")

  if (!value || !item || item == lastInvoicedItem) return

  localStorage.setItem("last-invoiced-item", item)
  try {
    await GtmService.invoiceChargedEvent(UserFlow.COACHING, invoiceId, value, [item as StripePrice])
  } catch (error) {
    console.error("Error reporting coaching invoice charge to Google Tag Manager", error)
  }
}

//  LIFECYCLE HOOKS
onMounted(async () => {
  if (isCoachingConfirmedPage.value) await reportInvoiceChargeToGoogleTagManager()
  else await reportOrderToGoogleTagManager()
  if (!coachingStore.hasAssignedCoach) await coachingStore.loadAssignedCoach()
})
</script>

<style scoped lang="scss">
$content-width: 820px;

.i-coaching-conirmation {
  .content {
    @include grid($cols: 1fr, $rows: auto, $gap: 0);

    .head {
      @include display-sm;

      text-align: center;
      font-weight: 700;
      margin-bottom: 24px;

      @media screen and (min-width: $breakpoint-md) {
        @include display-md;
      }
    }

    .gradient {
      background-image: linear-gradient(90deg, #4a9dff 0%, #be4fff 100%);
      color: transparent;
      background-clip: text;

      &.mini {
        @include text-xl;
      }
    }

    .support {
      @include flex(column, center, center);
      @include text-md;

      text-align: center;
      font-weight: 600;

      @media screen and (min-width: $breakpoint-md) {
        @include text-xl;
      }

      span {
        @include display-xs;

        @media screen and (min-width: $breakpoint-md) {
          @include display-md;
        }
      }
    }

    > section {
      @include flex(column, center, center, $gap: 35px);

      padding: 40px 0;

      > div {
        max-width: $content-width;
        margin-right: 15px;
        margin-left: 15px;

        @media screen and (min-width: $breakpoint-sm) {
          margin-right: 40px;
          margin-left: 40px;
        }

        @media screen and (min-width: $breakpoint-md) {
          width: 100%;
        }

        @media screen and (min-width: $breakpoint-lg) {
          margin-right: auto;
          margin-left: auto;
        }
      }
    }
  }
}
</style>
